import { createAsyncThunk } from '@reduxjs/toolkit';

import l10n from '../../shared/strings';
import { countImportPayloadStats } from '../../components/ScreenImportProducts/helpers/buildBatchImportPayload';
import getBatchImportWarningMessage from '../../helpers/products/getBatchImportWarningMessage';
import { getSavedSearches } from '../savedSearches/actions';
import { getProducts } from './getProducts';

const ip = l10n.IMPORT_PRODUCTS;

/**
 * Sends a batch of validated products to the server for creation/update.
 *
 * Payload shape:
 * {
 *   importMode: 'addAndUpdate' | 'add' | 'update',
 *   layer: 'master' | 'masterAndStores' | 'stores',
 *   columnConfig: {
 *     [fieldKey: string]: { emptyCellBehavior: UI label },
 *   },
 *   variantImport: {
 *     mode: 'none' | 'withVariants',
 *     parentSkuColumn?: string,
 *     optionColumns?: string[],
 *   },
 *   products: Array<ImportProduct>,
 * }
 *
 * ImportProduct (mode `none`):
 * {
 *   sku: string,
 *   title?: string,
 *   vendor?: string,
 *   tags?: string[],
 *   meta?: Record<string, unknown>,
 *   sourceRowIndex?: number,
 * }
 *
 * ImportProduct (mode `withVariants`):
 * {
 *   sku: string,
 *   title?: string,
 *   meta?: Record<string, unknown>,
 *   sourceRowIndex?: number,
 *   variants: Array<{
 *     sku: string,
 *     options: Array<{ name: string, value: string }>,
 *     title?: string,
 *     meta?: Record<string, unknown>,
 *     sourceRowIndex?: number,
 *   }>,
 * }
 */
const emptyCellBehaviorMap = {
  [ip.emptyCellBehaviorSkipLabel]: 'Skip',
  [ip.emptyCellBehaviorEraseLabel]: 'Erase',
  [ip.emptyCellBehaviorAddLabel]: 'Add',
  [ip.emptyCellBehaviorOverwriteLabel]: 'Overwrite',
};
// eslint-disable-next-line import/prefer-default-export
export const batchImport = createAsyncThunk(
  'products/batchImport',
  async (importPayload, {
    dispatch,
    extra: {
      sdk, Logger, showErrorDialog, Toast,
    },
  }) => {
    /** Convert the empty cell behavior labels to the backend enum values */
    const { columnConfig, ...rest } = importPayload;
    const columnConfigPayload = Object.fromEntries(
      Object.entries(columnConfig).map(([fieldKey, { emptyCellBehavior }]) => [
        fieldKey,
        { cellBehavior: emptyCellBehaviorMap[emptyCellBehavior] },
      ]),
    );
    const sdkPayload = { ...rest, columnConfig: columnConfigPayload };
    const { productCount, variantCount } = countImportPayloadStats(sdkPayload);

    try {
      // SDK methods return AxiosResponse — body is in `.data`.
      const { data } = await sdk.products.batchImport(sdkPayload);
      Logger.log('UI', 'products_batch_imported', {
        import_mode: importPayload.importMode,
        layer: importPayload.layer,
        variant_import_mode: importPayload.variantImport?.mode,
        products_count: productCount,
        variants_count: variantCount,
      });
      if (!data.background) {
        sessionStorage.setItem('productsBatchImport.suppressToast', '1');
      }
      try {
        await dispatch(getProducts()).unwrap();
      } catch {
        // Import succeeded; getProducts already logs and shows a toast on failure.
      }
      // Sync imports create a saved search before responding; refresh the tree now.
      // Background imports refresh from the products.batchImport.complete event instead.
      if (!data.background && data.savedSearchId) {
        try {
          await dispatch(getSavedSearches()).unwrap();
        } catch {
          // Import succeeded; getSavedSearches already logs and shows a toast on failure.
        }
      }
      // Sync: wizard shows the result UI; show SS soft-fail as a separate warning toast.
      // Background: PubSubRouter shows this when products.batchImport.complete arrives.
      if (!data.background && data.warning) {
        const warningMessage = getBatchImportWarningMessage(data.warning);
        if (warningMessage) {
          Toast(warningMessage, { type: 'warning', autoClose: false });
        }
      }
      return data;
    } catch (error) {
      const errorMessage = 'Could not import products';
      showErrorDialog(errorMessage);
      Logger.error(
        new Error('Error batch importing products'),
        { error },
        ['BatchImportProductsFailed', { errorMessage }],
      );
      throw error;
    }
  },
);
